// PWA витрины (fd-web-04): регистрация сервис-воркера, событие установки
// (`beforeinstallprompt`) и подписка на Web Push.
import { useSyncExternalStore } from "react";

import { api } from "@/api";

type BeforeInstallPromptEvent = Event & {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => {
	for (const l of listeners) l();
};

/** Вызывается один раз из frontend.tsx до монтирования React. */
export function initPwa() {
	if (typeof window === "undefined") return;
	window.addEventListener("beforeinstallprompt", (event) => {
		// Браузерную мини-плашку не показываем — у витрины своя кнопка.
		event.preventDefault();
		deferredPrompt = event as BeforeInstallPromptEvent;
		emit();
	});
	window.addEventListener("appinstalled", () => {
		installed = true;
		deferredPrompt = null;
		emit();
	});
	// Сервис-воркер — только у собранной витрины: в дев-режиме с HMR он бы
	// держал старые файлы.
	if ("serviceWorker" in navigator && !import.meta.hot) {
		window.addEventListener("load", () => {
			navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => undefined);
		});
	}
}

export const isStandalone = (): boolean =>
	typeof window !== "undefined" &&
	(window.matchMedia?.("(display-mode: standalone)").matches ||
		(navigator as Navigator & { standalone?: boolean }).standalone === true);

/** iPhone/iPad в Safari: установки по кнопке нет, только «На экран Домой». */
export const isIosSafari = (): boolean => {
	if (typeof navigator === "undefined") return false;
	const ua = navigator.userAgent;
	const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
	return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|YaBrowser/.test(ua);
};

export type InstallState = { canPrompt: boolean; installed: boolean };

let snapshot: InstallState = { canPrompt: false, installed: false };
const getSnapshot = (): InstallState => {
	const next = { canPrompt: deferredPrompt != null, installed: installed || isStandalone() };
	if (next.canPrompt !== snapshot.canPrompt || next.installed !== snapshot.installed) snapshot = next;
	return snapshot;
};
const subscribe = (cb: () => void) => {
	listeners.add(cb);
	return () => listeners.delete(cb);
};

export function useInstallState(): InstallState {
	return useSyncExternalStore(subscribe, getSnapshot, () => snapshot);
}

/** Показать системное окно установки. true — покупатель согласился. */
export async function promptInstall(): Promise<boolean> {
	const event = deferredPrompt;
	if (!event) return false;
	deferredPrompt = null;
	emit();
	await event.prompt();
	const choice = await event.userChoice;
	return choice.outcome === "accepted";
}

// ── «Один раз за сессию» ─────────────────────────────────────────────────────
// Плашку видит первое место, где она смогла показаться (главная или профиль);
// после закрытия или в другом месте той же сессии — уже нет.
const SESSION_KEY = "osmix:pwa-install-prompt";
let claimedBy: string | null = (() => {
	try {
		return typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(SESSION_KEY);
	} catch {
		return null;
	}
})();

export function claimInstallPrompt(placement: string): boolean {
	if (claimedBy === null) {
		claimedBy = placement;
		try {
			sessionStorage.setItem(SESSION_KEY, placement);
		} catch {
			// приватный режим — ограничение только на эту загрузку
		}
	}
	return claimedBy === placement;
}

export function dismissInstallPrompt() {
	claimedBy = "dismissed";
	try {
		sessionStorage.setItem(SESSION_KEY, "dismissed");
	} catch {
		// приватный режим
	}
}

// ── Web Push ─────────────────────────────────────────────────────────────────

export type PushSupport = "supported" | "unsupported" | "ios-needs-install";

export const pushSupport = (): PushSupport => {
	if (typeof window === "undefined") return "unsupported";
	const has = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
	if (has) return "supported";
	// На iPhone push появляется только у витрины, установленной на экран Домой (iOS 16.4+).
	if (isIosSafari() && !isStandalone()) return "ios-needs-install";
	return "unsupported";
};

const urlBase64ToUint8Array = (base64: string): Uint8Array<ArrayBuffer> => {
	const padding = "=".repeat((4 - (base64.length % 4)) % 4);
	const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
	const out = new Uint8Array(new ArrayBuffer(raw.length));
	for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
	return out;
};

export async function currentPushSubscription(): Promise<PushSubscription | null> {
	if (pushSupport() !== "supported") return null;
	const reg = await navigator.serviceWorker.getRegistration("/");
	return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Подписать браузер: разрешение → подписка у push-сервиса → запись на шлюзе. */
export async function enablePush(publicKey: string): Promise<"ok" | "denied"> {
	const permission = await Notification.requestPermission();
	if (permission !== "granted") return "denied";
	const reg = (await navigator.serviceWorker.getRegistration("/")) ?? (await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
	await navigator.serviceWorker.ready;
	const sub =
		(await reg.pushManager.getSubscription()) ??
		(await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
	const json = sub.toJSON();
	await api.push.subscribe({
		endpoint: sub.endpoint,
		keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
	});
	return "ok";
}

/** Отписать браузер: сначала шлюз (прекращает отправку), потом push-сервис. */
export async function disablePush(): Promise<void> {
	const sub = await currentPushSubscription();
	if (!sub) return;
	await api.push.unsubscribe(sub.endpoint);
	await sub.unsubscribe().catch(() => false);
}
